import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import { createScheduleWindow, updateScheduleWindow } from '../api/client';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

function ScheduleWindowForm({ pipelineId, pipelineName, schedule, onClose, onSuccess }) {
  const isEditing = !!schedule;

  const [formData, setFormData] = useState({
    name: schedule?.name || '',
    description: schedule?.description || '',
    schedule_type: schedule?.schedule_type || 'one_time',
    // One-time fields
    one_time_start: schedule?.one_time_start
      ? new Date(schedule.one_time_start).toISOString().slice(0, 16)
      : '',
    one_time_end: schedule?.one_time_end
      ? new Date(schedule.one_time_end).toISOString().slice(0, 16)
      : '',
    // Recurring fields
    days_of_week: schedule?.days_of_week || [0, 1, 2, 3, 4], // Default to weekdays
    recurring_start_time: schedule?.recurring_start_time || '09:00',
    recurring_end_time: schedule?.recurring_end_time || '17:00',
    recurring_start_date: schedule?.recurring_start_date
      ? new Date(schedule.recurring_start_date).toISOString().slice(0, 16)
      : '',
    recurring_end_date: schedule?.recurring_end_date
      ? new Date(schedule.recurring_end_date).toISOString().slice(0, 16)
      : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDayToggle = (dayValue) => {
    const currentDays = formData.days_of_week;
    const newDays = currentDays.includes(dayValue)
      ? currentDays.filter((d) => d !== dayValue)
      : [...currentDays, dayValue].sort((a, b) => a - b);
    setFormData({
      ...formData,
      days_of_week: newDays,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        schedule_type: formData.schedule_type,
      };

      if (formData.schedule_type === 'one_time') {
        payload.one_time_start = new Date(formData.one_time_start).toISOString();
        payload.one_time_end = new Date(formData.one_time_end).toISOString();
      } else {
        payload.days_of_week = formData.days_of_week;
        payload.recurring_start_time = formData.recurring_start_time + ':00';
        payload.recurring_end_time = formData.recurring_end_time + ':00';
        if (formData.recurring_start_date) {
          payload.recurring_start_date = new Date(formData.recurring_start_date).toISOString();
        }
        if (formData.recurring_end_date) {
          payload.recurring_end_date = new Date(formData.recurring_end_date).toISOString();
        }
      }

      if (isEditing) {
        await updateScheduleWindow(schedule.id, payload);
      } else {
        await createScheduleWindow(pipelineId, payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    if (!formData.name) return false;
    if (formData.schedule_type === 'one_time') {
      return formData.one_time_start && formData.one_time_end;
    } else {
      return (
        formData.days_of_week.length > 0 &&
        formData.recurring_start_time &&
        formData.recurring_end_time
      );
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Schedule' : 'Create Schedule'}
        {pipelineName && (
          <Typography variant="subtitle2" color="text.secondary">
            Pipeline: {pipelineName}
          </Typography>
        )}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Schedule Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
              placeholder="e.g., Weekday Operations"
            />

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description"
            />

            <FormControl fullWidth>
              <InputLabel>Schedule Type</InputLabel>
              <Select
                name="schedule_type"
                value={formData.schedule_type}
                onChange={handleChange}
                label="Schedule Type"
              >
                <MenuItem value="one_time">One-Time Window</MenuItem>
                <MenuItem value="recurring">Recurring</MenuItem>
              </Select>
            </FormControl>

            {formData.schedule_type === 'one_time' ? (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Times are in UTC
                </Typography>
                <TextField
                  label="Start Date & Time (UTC)"
                  name="one_time_start"
                  type="datetime-local"
                  value={formData.one_time_start}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date & Time (UTC)"
                  name="one_time_end"
                  type="datetime-local"
                  value={formData.one_time_end}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </>
            ) : (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Select days and times (UTC)
                </Typography>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Days of Week
                  </Typography>
                  <FormGroup row>
                    {DAYS_OF_WEEK.map((day) => (
                      <FormControlLabel
                        key={day.value}
                        control={
                          <Checkbox
                            checked={formData.days_of_week.includes(day.value)}
                            onChange={() => handleDayToggle(day.value)}
                          />
                        }
                        label={day.label}
                      />
                    ))}
                  </FormGroup>
                  {formData.days_of_week.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {formData.days_of_week.map((d) => (
                        <Chip
                          key={d}
                          label={DAYS_OF_WEEK.find((day) => day.value === d)?.label}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Start Time (UTC)"
                    name="recurring_start_time"
                    type="time"
                    value={formData.recurring_start_time}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Time (UTC)"
                    name="recurring_end_time"
                    type="time"
                    value={formData.recurring_end_time}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Optional: Limit recurring schedule to a date range
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Start Date (optional)"
                    name="recurring_start_date"
                    type="datetime-local"
                    value={formData.recurring_start_date}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Date (optional)"
                    name="recurring_end_date"
                    type="datetime-local"
                    value={formData.recurring_end_date}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isFormValid()}
          >
            {loading ? 'Saving...' : isEditing ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ScheduleWindowForm;
